import { Grid, Skeleton, Table, TableHead, TableBody, TableRow, TableCell, TableContainer } from '@mui/material';
import React from 'react';
import { CerebroSecondaryButton, CerebroText } from '../../../shared/components';
import { EmptyGuid } from '../../../shared/constants';
import { TermSheetFinancialReporting, TermSheetRevision } from '../../../shared/models';
import { EnumUtils } from '../../../shared/utils';
import { ReportingDeleteDialog } from './ReportingDeleteDialog';
import { ReportingForm } from './ReportingForm';

interface ReportingPageProps {
	dataRoomGuid: string | undefined;
	termSheetGuid: string | undefined;
	revisionGuid: string | undefined;
	revision: TermSheetRevision | null;
	isLoading: boolean;
}

export const ReportingPage: React.FC<ReportingPageProps> = ({
	dataRoomGuid,
	termSheetGuid,
	revisionGuid,
	revision,
	isLoading,
}) => {
	const [reportingBeingEdited, setReportingBeingEdited] = React.useState<TermSheetFinancialReporting | null>(null);
	const [reportingBeingDeleted, setReportingBeingDeleted] = React.useState<TermSheetFinancialReporting | null>(null);
	const reportingProvided = revision?.reportingProvided ?? true;

	const onAddReporting = () => {
		setReportingBeingEdited({
			id: 0,
			guid: EmptyGuid,
			reportingQuality: 'None',
			frequency: 'Annually',
			daysToDeliver: 0,
		} as TermSheetFinancialReporting);
	};

	const closeDrawersAndDialogs = () => {
		setReportingBeingEdited(null);
		setReportingBeingDeleted(null);
	};

	if (isLoading) {
		return (
			<>
				<Skeleton height={60} />
				<Skeleton height={60} />
				<Skeleton height={60} />
			</>
		);
	}

	return (
		<>
			{reportingProvided && (
				<>
					<CerebroSecondaryButton onClick={() => onAddReporting()} sx={{ marginBottom: 5 }}>
						Add
					</CerebroSecondaryButton>

					<Grid container spacing={2} alignItems="stretch">
						<TableContainer>
							<Table>
								<TableHead>
									<TableRow>
										<TableCell>Quality</TableCell>
										<TableCell>Frequency</TableCell>
										<TableCell># Days</TableCell>
										<TableCell>&nbsp;</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{revision?.reporting.map(reporting => {
										return (
											<TableRow key={reporting.guid}>
												<TableCell>
													{EnumUtils.GetCompanyFinancialStatementTypesDescription(
														reporting.reportingQuality
													)}
												</TableCell>
												<TableCell>
													{EnumUtils.GetFacilityAmortizationPeriodsDescription(
														reporting.frequency
													)}
												</TableCell>
												<TableCell>{reporting.daysToDeliver}</TableCell>
												<TableCell>
													<CerebroSecondaryButton
														onClick={() => setReportingBeingEdited(reporting)}
													>
														Edit
													</CerebroSecondaryButton>
													<CerebroSecondaryButton
														onClick={() => setReportingBeingDeleted(reporting)}
													>
														Delete
													</CerebroSecondaryButton>
												</TableCell>
											</TableRow>
										);
									})}
									{(!revision?.reporting || revision?.reporting.length == 0) && (
										<TableRow>
											<TableCell sx={{ textAlign: 'center' }} colSpan={4}>
												No reporting has been added for the term sheet
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>
					</Grid>
				</>
			)}

			{!reportingProvided && (
				<CerebroText>
					Reporting has been marked as not provided. To update this setting navigate to the general tab.
				</CerebroText>
			)}

			<ReportingForm
				dataRoomGuid={dataRoomGuid}
				termSheetGuid={termSheetGuid}
				revisionGuid={revisionGuid}
				reporting={reportingBeingEdited}
				close={closeDrawersAndDialogs}
				isLoading={isLoading}
			/>

			<ReportingDeleteDialog
				dataRoomGuid={dataRoomGuid}
				termSheetGuid={termSheetGuid}
				revisionGuid={revisionGuid}
				reporting={reportingBeingDeleted}
				close={closeDrawersAndDialogs}
			/>
		</>
	);
};
